import { useChatReadFilter, setChatReadTimestamp } from 'src/hooks/useChatReadFilter'
import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { useQuery } from '@apollo/react-hooks'
import {
  makeStyles,
  useScrollTrigger,
  Drawer,
  AnimatePresence,
  motion,
  AlertDialog,
  useClientOptions,
  useDialogs,
  useMediaQuery,
  useUser
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithUseStyles } from '@paintscout/ui'
import ActivityIcon from '@material-ui/icons/AccessTime'
import ChatIcon from '@material-ui/icons/ChatBubble'
import MoneyIcon from '@material-ui/icons/CreditCard'
import EmailIcon from '@material-ui/icons/Email'
import GradeIcon from '@material-ui/icons/Grade'
import InfoIcon from '@material-ui/icons/Info'
import SettingsIcon from '@material-ui/icons/Settings'
import SubjectIcon from '@material-ui/icons/Subject'
import type { ActivitiesForDocQuery, MessagesQuery, UnreadMessagesQuery } from '@paintscout/api'
import { FullActivityFragmentDoc, useUnreadMessagesQuery } from '@paintscout/api'
import {
  getFeature,
  getFollowUps,
  getObjectLabels,
  getQuotePresentation,
  hasIntegrationInterface
} from '@paintscout/util/builder'
import { EditQuoteFollowUpDialog, EditTermsDialog, QuoteContext, useWisetack } from '@paintscout/react-quote'
import { gql } from 'apollo-boost'
import type { QuoteDocument } from 'paintscout'
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import Moment from 'react-moment'
import type { FeatureProps } from '../Feature/Feature'
import { calculateHeaderHeight } from '../Header/headerHeight'
import QuoteActivity from './pages/QuoteActivity'
import QuoteAdvancedForm from './pages/QuoteAdvancedForm'
import QuoteChat from './pages/QuoteChat'
import QuoteDetailsForm from './pages/QuoteDetailsForm'
import QuotePaymentsAndLending from './pages/QuotePaymentsAndLending'
import QuotePresentationForm from './pages/QuotePresentationForm'
import QuoteSidebarIndex from './QuoteSidebarIndex'
import QuoteSidebarTitle from './QuoteSidebarTitle'

const useStyles = makeStyles<
  Theme,
  QuoteSidebarProps & {
    isSticky: boolean
    isTrial: boolean
    isMasquerading: boolean
    isCorporate: boolean
    isTemplate: boolean
  }
>(
  (theme: Theme) => ({
    root: {
      color: theme.palette.text.primary,
      height: '100%',
      marginRight: -267,
      '@media print': {
        display: 'none'
      }
    },
    sidebar: (props) => {
      const headerHeight = calculateHeaderHeight({
        isMasquerading: props.isMasquerading,
        isCorporate: props.isCorporate,
        isTrial: props.isTrial,
        isSticky: props.isSticky,
        isTemplate: props.isTemplate
      })

      return {
        padding: 0,
        background: '#fff',
        borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
        borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
        position: 'fixed',
        top: 0,
        width: 267,
        paddingTop: headerHeight,
        transition: 'padding 0.3s',
        // maxHeight: `calc(100vh)`,

        // this is not supported by Safari, but it prevents scrolling the parent automatically when you've
        // reached the end of this element's scroll
        overscrollBehavior: 'contain',

        '& $page': {
          paddingBottom: theme.spacing(4)
        },
        [theme.breakpoints.up(1330)]: {
          width: '340px'
        },
        [theme.breakpoints.up(1400)]: {
          width: '367px'
        }
      }
    },
    title: {
      position: 'sticky',
      width: '100%'
    },
    sidebarContent: {
      height: '100%'
    },
    page: {
      overflowY: 'scroll',
      // if props.isSticky, we'll shorten the max height so contents don't get cut off by the sidebar shifting down
      maxHeight: (props) => `calc(100vh - ${theme.paintscout.header.height.md} ${props.isSticky ? ' - 40px' : ''})`
    },
    drawerRoot: {
      '@media print': {
        display: 'none'
      }
    },
    drawerPaper: {
      width: 367,
      display: 'block',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    drawerContent: {
      overflow: 'hidden',

      [theme.breakpoints.up(1490)]: {
        borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.25)'
    }
  }),
  { name: 'QuoteSidebar' }
)

export interface QuoteSidebarProps {
  classes?: WithUseStyles<typeof useStyles>['classes']
  open?: boolean
  editable?: boolean
  permissionToEdit?: boolean
  permissionToEditQuote?: boolean
  permissionToSendQuote?: boolean
  permissionToEditPresentationSection?: boolean
  currentPage?: string
  onToggleEdit?: (editStatus: boolean) => any
  onClose?: () => any
  onClick?: (event: React.MouseEvent) => any
}

export interface QuoteSidebarState {
  previousPage: string
}

export interface QuoteSidebarPage {
  key: string
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  component?: React.ComponentType<any>
  dialog?: React.ComponentType<any>
  features?: FeatureProps['required']
  editable?: boolean
  badgeContent?: React.ReactNode
  shrink?: boolean
  onClick?: (event: React.MouseEvent) => any
}

function QuoteSidebar(props: QuoteSidebarProps) {
  const {
    open,
    onClick,
    editable,
    onClose,
    onToggleEdit,
    permissionToEdit,
    permissionToEditQuote,
    permissionToSendQuote,
    permissionToEditPresentationSection
  } = props
  const isSticky = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100
  })
  const { options } = useClientOptions()
  const { openDialog, dismissDialog } = useDialogs()
  const { user, isSuperadmin, isSupport, isAdmin, isSales, preferences, isTrial, isCorporate, isMasquerading } =
    useUser()
  const { quote, updateQuote, onSidebarPageChange, hasActiveAccount } = useContext(QuoteContext)
  const isTemplate = quote.type === 'quoteTemplate'
  const classes = useStyles({ isSticky, isTrial, isMasquerading, isCorporate, isTemplate, ...props })

  const { hasWisetack } = useWisetack({ user, options, preferences })
  const canBeSales = isAdmin || isSales || isSuperadmin || isSupport

  const stagedPayoutsFeature = options.options.integrations?.wisetack?.config?.progressPaymentsEnabled

  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const showWisetackDepositAmount =
    hasWisetack && !quote?.is_invoice && stagedPayoutsFeature && !quote?.totals.amount_paid && !quote?.depositInfo

  const showPaymentsSidebar = hasStripe || hasStripeW1d || hasPayrix || showWisetackDepositAmount

  const objectLabels = getObjectLabels({ options })
  const isMobile = useMediaQuery(1600)

  const currentPage = props.currentPage ?? 'index'
  const followUps = getFollowUps({ preferences, options })
  const sentFollowUps = Object.keys(quote?.followUps ?? {})
    .map((followUpKey) => {
      if (quote.followUps?.[followUpKey]?.sent) {
        return {
          key: followUpKey,
          ...quote.followUps[followUpKey]
        }
      } else {
        return null
      }
    })
    .filter(Boolean)

  let activeFollowUps = (followUps.order ?? [])
    .map((key) => followUps.values[key])
    // only show followUps that would apply to this quote
    .filter((followUp) => followUp?.active && !!quote.is_invoice === !!followUp.isInvoice)
    .filter((followUp) => {
      // If follow-up created is older than quote sent date, don't show it unless it is retroactive followup
      if (followUp.created > quote.dates?.client?.sent && !followUp.retroactive) {
        return false
      }
      // If follow up already sent
      if (sentFollowUps.filter((followUpInner) => followUpInner.key === followUp.key).length > 0) {
        return false
      }
      return true
    })
    .map((followUp) => ({
      ...followUp,
      // if quote has follow up disabled, mark it as inactive for the FollowUp table
      active: !(quote.followUps?.[followUp.key]?.disabled && !quote.followUps?.[followUp.key]?.sendText)
    }))
    .filter((followUp) => followUp.active)
  // Additional filter if quote
  if (!quote.is_invoice) {
    activeFollowUps = activeFollowUps.filter(
      (followUp) =>
        followUp.quoteTypes.includes(quote.int_ext.value) ||
        quote.int_ext.value === 'all' ||
        followUp.quoteTypes.includes('__all__')
    )
  }

  let followUpSubtitle = ''
  if (quote.followUpsDisabled) {
    followUpSubtitle = 'Follow-Ups Disabled'
  } else if (activeFollowUps.length) {
    followUpSubtitle =
      `${activeFollowUps.length} Scheduled` + `${sentFollowUps.length ? `, ${sentFollowUps.length} Sent` : ''}`
  } else {
    followUpSubtitle = 'No Follow-Ups Scheduled'
  }

  const hasPresentation =
    (getFeature({ options, path: 'presentation.enabled' }) || isSuperadmin || isSupport) &&
    (!quote?.is_invoice || getFeature({ options, path: 'presentation.presentationsOnInvoices' }))

  const presentationTemplate = getQuotePresentation({ options, quote, isWorkOrder: false, isProductOrderForm: false })

  const activitiesQuery = useQuery<ActivitiesForDocQuery & UnreadMessagesQuery & MessagesQuery>(
    gql`
      query sidebarInfo($id: String!) {
        activitiesForDoc(id: $id, limit: 1) {
          activities {
            ...FullActivity
          }
        }
      }
      ${FullActivityFragmentDoc}
    `,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: usePollingInterval(50000),
      skip: currentPage !== 'index' || quote.type === 'quoteTemplate',
      variables: {
        id: quote._id
      }
    }
  )

  const shouldPollUnread = currentPage !== 'chat' && open
  const { data: unreadMessagesQueryData, refetch } = useUnreadMessagesQuery({
    skip: quote.type === 'quoteTemplate',
    fetchPolicy: 'cache-and-network',
    pollInterval: usePollingInterval(shouldPollUnread ? 30000 : 0),
    variables: {
      quoteId: quote._id
    }
  })

  const [lastActivity] = activitiesQuery.data?.activitiesForDoc?.activities ?? [null]

  const unreadMessages = useChatReadFilter(quote._id, unreadMessagesQueryData?.unreadMessages ?? [])
  const [lastUnreadMessage] = unreadMessages.length > 0 ? unreadMessages : [null]

  const prevPageRef = useRef(currentPage)

  useEffect(() => {
    const prevPage = prevPageRef.current
    prevPageRef.current = currentPage

    // Opening chat = reading messages — set timestamp so stale ES results are filtered
    if (currentPage === 'chat' && prevPage !== 'chat') {
      setChatReadTimestamp(quote._id)
    }

    // Refetch when navigating to index (but not from chat — readMessages already handled it)
    if (currentPage === 'index' && prevPage !== 'chat') {
      refetch({
        quoteId: quote._id
      })
    }
  }, [currentPage])

  const pages: QuoteSidebarPage[] = [
    { key: 'index', title: 'Menu', subtitle: '' },
    !isTemplate && {
      key: 'activity',
      title: 'Activity',
      subtitle: lastActivity?.details?.timestamp && (
        <Moment
          date={lastActivity.details.timestamp}
          fromNow={true}
          element={({ children }) => <span>Last activity&nbsp;{children}</span>}
        />
      ),
      icon: <ActivityIcon />,
      component: QuoteActivity
    },
    !isTemplate && {
      key: 'chat',
      title: 'Chat',
      subtitle: lastUnreadMessage?.details?.timestamp ? (
        <Moment
          date={lastUnreadMessage.details.timestamp}
          fromNow={true}
          element={({ children }) => <span>New message received&nbsp;{children}</span>}
        />
      ) : (
        `No new messages`
      ),
      badgeContent: unreadMessages.length || null,
      icon: <ChatIcon />,
      component: QuoteChat,
      features: {
        'quotes.conversations.enabled': true
      }
    },
    getFeature({ options, preferences, path: 'quotes.followUps.enabled' }) &&
      canBeSales && {
        key: 'followup',
        title: 'Follow-Ups',
        subtitle: followUpSubtitle,
        icon: <EmailIcon />,
        dialog: EditQuoteFollowUpDialog
      },
    {
      key: 'details',
      title: 'Calculations',
      subtitle: 'Hourly Rate, Discount',
      icon: <InfoIcon />,
      component: QuoteDetailsForm
    },
    hasPresentation
      ? {
          key: 'presentation',
          title: 'Presentation',
          subtitle: 'Current: ' + (presentationTemplate?.label ?? 'Default Presentation'),
          icon: <GradeIcon />,
          component: QuotePresentationForm
        }
      : null,
    {
      key: 'terms',
      title: 'Contract Terms',
      subtitle: 'Special Conditions, Warranties',
      icon: <SubjectIcon />,
      dialog: EditTermsDialog
    },
    {
      key: 'advanced',
      title: 'Advanced',
      subtitle: 'Advanced Options',
      icon: <SettingsIcon />,
      component: QuoteAdvancedForm,
      features: {
        'quotes.sidebar.advanced.enabled': true
      }
    },
    showPaymentsSidebar
      ? {
          key: 'payments',
          title: 'Payments & Lending',
          subtitle: 'Allow Payments, Deposit Amount',
          shrink: true,
          icon: <MoneyIcon />,
          component: QuotePaymentsAndLending
        }
      : null
  ].filter((menuItem) => !!menuItem)

  const [sidebarState, setSidebarState] = useState<QuoteSidebarState>({
    // currentPage: 'index',
    previousPage: null
  })

  if (!pages.length) {
    return null
  }

  const pageIndex = getPageIndexByName(currentPage, pages)

  const handleCloseSidebar = () => {
    onSidebarPageChange('index')

    if (onClose) {
      onClose()
    }
  }

  function handleChangeIndex(index: number) {
    const page = pages[index]

    if (page.dialog) {
      if (!permissionToEdit) {
        const title = 'Cannot Edit'
        const message = `You do not have sufficient permissions to edit Terms`
        openDialog(AlertDialog, {
          title,
          message,
          onConfirm: dismissDialog
        })
      } else {
        openDialog(page.dialog, {
          editable,
          permissionToEdit,
          objectLabels,
          quote,
          hasActiveAccount,
          onToggleEdit: (ev: boolean) => {
            onToggleEdit(ev)
          },
          onConfirm: (updatedQuote: QuoteDocument, isDirty: boolean) => {
            if (isDirty) {
              updateQuote({ quote: updatedQuote, autosave: isDirty })
            }
            dismissDialog()
          },
          onCancel: () => {
            dismissDialog()
          }
        })
      }
    } else {
      onSidebarPageChange(page.key)

      setSidebarState({ ...sidebarState, previousPage: currentPage })
    }
  }

  const title = (
    <QuoteSidebarTitle
      pageKey={pages[pageIndex].key}
      onCloseClick={() => handleCloseSidebar()}
      onBackClick={
        pageIndex > 0
          ? () => {
              handleChangeIndex(0)
            }
          : null
      }
      title={pages[pageIndex].title}
      shrink={pages[pageIndex].shrink}
    />
  )

  return (
    <AnimatePresence initial={false}>
      {(open || isMobile) && (
        <motion.div
          className={classes.root}
          variants={{
            show: {
              opacity: 1
            },
            hide: {
              opacity: 0
            }
          }}
          initial="hide"
          animate="show"
          exit="hide"
        >
          {isMobile ? (
            <Drawer
              variant="temporary"
              anchor={'right'}
              open={open}
              onClose={() => handleCloseSidebar()}
              BackdropProps={{
                classes: {
                  root: classes.backdrop
                }
              }}
              classes={{
                root: classes.drawerRoot,
                paper: classes.drawerPaper
              }}
              ModalProps={{
                keepMounted: true // Better open performance on mobile.
              }}
            >
              {title}
              <DrawerContent
                classes={classes}
                pages={pages}
                pageIndex={pageIndex}
                previousPageIndex={getPageIndexByName(sidebarState.previousPage, pages)}
                onChangeIndex={handleChangeIndex}
                editable={editable}
                permissionToEdit={permissionToEdit}
                permissionToEditQuote={permissionToEditQuote}
                permissionToSendQuote={permissionToSendQuote}
                permissionToEditPresentationSection={permissionToEditPresentationSection}
                onClick={onClick}
                onToggleEdit={onToggleEdit}
              />
            </Drawer>
          ) : (
            <div className={classes.sidebar}>
              {title}
              <DrawerContent
                classes={classes}
                pages={pages}
                pageIndex={pageIndex}
                previousPageIndex={getPageIndexByName(sidebarState.previousPage, pages)}
                onChangeIndex={handleChangeIndex}
                editable={editable}
                permissionToEdit={permissionToEdit}
                permissionToEditQuote={permissionToEditQuote}
                permissionToSendQuote={permissionToSendQuote}
                permissionToEditPresentationSection={permissionToEditPresentationSection}
                onClick={onClick}
                onToggleEdit={onToggleEdit}
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function getPageIndexByName(key: string, pages: QuoteSidebarPage[]) {
  return pages.findIndex((page) => page.key === key)
}

//////////////////////////////////////////

interface DrawerContentProps {
  classes: ReturnType<typeof useStyles>
  pageIndex: number
  previousPageIndex: number
  pages: QuoteSidebarPage[]
  onChangeIndex: (newIndex: number) => any
  editable?: boolean
  permissionToEdit?: boolean
  permissionToEditQuote?: boolean
  permissionToSendQuote?: boolean
  permissionToEditPresentationSection?: boolean
  onClick?: (event: React.MouseEvent) => any
  onToggleEdit?: (ev: boolean) => any
}

function DrawerContent({
  classes,
  pageIndex,
  previousPageIndex,
  pages,
  onChangeIndex,
  onClick,
  onToggleEdit,
  editable,
  permissionToEdit,
  permissionToEditQuote,
  permissionToSendQuote,
  permissionToEditPresentationSection
}: DrawerContentProps) {
  const ref = useRef<HTMLDivElement>(null)
  const page = pages[pageIndex]

  const isGoingBack = pageIndex < previousPageIndex

  useLayoutEffect(() => {
    if (ref && ref.current && ref.current.scrollTo) {
      ref.current.scrollTo({ top: 0 })
    }
  }, [pageIndex])

  return (
    <div ref={ref} className={classes.drawerContent}>
      <div style={{ position: 'relative' }}>
        <AnimatePresence initial={false} custom={{ pageIndex, previousPageIndex }}>
          <motion.div
            key={pageIndex}
            className={classes.page}
            custom={{ pageIndex, previousPageIndex }}
            variants={{
              initial: {
                x: isGoingBack ? -150 : 300,
                opacity: 0,
                overflow: 'hidden'
              },
              animate: {
                x: 0,
                zIndex: 1,
                opacity: 1,
                position: 'static',
                transitionEnd: {
                  overflow: 'auto'
                }
              },

              // we use the "custom" prop in AnimatePresence to keep the exit variant from using stale data
              exit: ({ pageIndex, previousPageIndex }) => {
                const isGoingBack = pageIndex < previousPageIndex
                return {
                  x: isGoingBack ? 300 : -150,
                  position: 'absolute',
                  overflow: 'hidden',
                  zIndex: 0,
                  opacity: 0
                }
              }
            }}
            initial={'initial'}
            animate={'animate'}
            exit={'exit'}
            transition={{
              damping: 300
            }}
          >
            {pageIndex === 0 ? (
              <QuoteSidebarIndex
                pages={pages}
                onClick={(name: string, _: React.MouseEvent) => {
                  const pageIndex = getPageIndexByName(name, pages)
                  onChangeIndex(pageIndex)
                }}
              />
            ) : (
              <page.component
                onClick={onClick}
                editable={editable}
                onToggleEdit={(ev: boolean) => {
                  onToggleEdit(ev)
                }}
                permissionToEdit={permissionToEdit}
                permissionToEditQuote={permissionToEditQuote}
                permissionToSendQuote={permissionToSendQuote}
                permissionToEditPresentationSection={permissionToEditPresentationSection}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default QuoteSidebar
